import React, { useState, useEffect } from "react";
import "./index.scss";
import {
  Box,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import CustomTable from "../../../core/custom-table";
import { HeaderConfig } from "./config";
import KnowledgeSharingFilters from "./knowledge-sharing-filters";
import { HTTP_METHODS, invokeApi } from "../../../utils/http-service";
import { HOSTNAME, REST_URLS } from "../../../utils/endpoints";
import { toast } from "react-toastify";
import { getFileNameFromUrl, PAGE_LIMIT } from "../../../utils";
import CustomButtonComponent from "../../../core/custom-button";
import DeleteModal from "../../../components/delete-modal";
import FormGenerator from "../../../core/form-generator";
import { getKnowledgeSharingFormConfig } from "./config";
import { handleFormValidation } from "../../../utils";
import SiFileUpload from "../../../core/file-uploader";

const KnowledgeSharing = () => {
  const [data, setData] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [isCreate, setIsCreate] = useState(false);
  const [editData, setEditData] = useState({});
  const [isDelete, setIsDelete] = useState({ isOpen: false, data: {} });
  const [filters, setFilters] = useState({
    page: 1,
    limit: PAGE_LIMIT,
    sortBy: "-createdAt",
  });
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [files, setFiles] = useState([]);
  const [previewUrl, setPreviewUrl] = useState(null);

  const ClickHandler = (type, data) => {
    switch (type) {
      case "EDIT":
        setIsCreate(true);
        setEditData(data);
        setFormData({ topic: data.topic });
        setFiles([{ url: data.document }]);
        break;
      case "DELETE":
        setIsDelete({ isOpen: true, data: data });
        break;
      case "PREVIEW":
        setPreviewUrl(data.document);
        break;
      default:
        break;
    }
  };
  const deleteHandler = () => {
    invokeApi(
      HTTP_METHODS.DELETE,
      `${HOSTNAME}${REST_URLS.KNOWLEDGE_SHARING}/${isDelete?.data?.id}`
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          toast.success("Deleted Successfully");
          setIsDelete({ isOpen: false, data: {} });
          loadData(filters);
        }
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to delete knowledge");
      });
  };

  const loadData = (filters) => {
    setIsLoading(true);
    invokeApi(
      HTTP_METHODS.GET,
      `${HOSTNAME}${REST_URLS.KNOWLEDGE_SHARING}`,
      null,
      filters
    )
      .then((res) => {
        if (res.message) {
          toast.error(res.message);
        } else {
          const result = res?.results?.map((ele, index) => ({
            ...ele,
            siNo: index + 1,
          }));
          setData({ ...res, results: result });
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load knowledge list");
      });
  };

  const submitHandler = () => {
    const isValid = handleFormValidation(
      getKnowledgeSharingFormConfig(),
      setErrors,
      formData
    );
    if (!files[0]) {
      toast.error("Document is required");
      return;
    }

    if (isValid && files[0]) {
      const formDataPayload = {
        topic: formData.topic,
        document: files[0]?.url,
      };

      if (editData?.id) {
        invokeApi(
          HTTP_METHODS.PUT,
          `${HOSTNAME}${REST_URLS.KNOWLEDGE_SHARING}/${editData.id}`,
          formDataPayload
        )
          .then((res) => {
            if (res.message) {
              toast.error(res.message);
            } else {
              toast.success("Knowledge updated successfully");
              setIsCreate(false);
              loadData({ page: 1, sortBy: "-createdAt", limit: PAGE_LIMIT });
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error("Failed to update knowledge");
          });
      } else {
        invokeApi(
          HTTP_METHODS.POST,
          `${HOSTNAME}${REST_URLS.KNOWLEDGE_SHARING}`,
          formDataPayload
        )
          .then((res) => {
            if (res.message) {
              toast.error(res.message);
            } else {
              toast.success("Knowledge shared successfully");
              setIsCreate(false);
              loadData({ page: 1, sortBy: "-createdAt", limit: PAGE_LIMIT });
            }
          })
          .catch((err) => {
            console.error(err);
            toast.error("Failed to share knowledge");
          });
      }
    }
  };

  const callBack = (newFile) => {
    setFiles([newFile]);
  };

  useEffect(() => {
    loadData(filters);
  }, []);

  return (
    <div className="page-body">
      <div className="view-detailed-ticket">
        <div>
          <p className="tab-name">Admin</p>
          <p className="section-name">Knowledge Sharing</p>
        </div>
      </div>
      <div>
        <div className="create-con">
          <p className="primary-text">Knowledge Sharing</p>
          <div>
            <CustomButtonComponent
              variant="contained"
              color="primary"
              onClick={() => {
                setIsCreate(true);
                setEditData({});
                setFormData({});
                setFiles([]);
              }}
            >
              Share Knowledge
            </CustomButtonComponent>
          </div>
        </div>
        <CustomTable
          header={HeaderConfig}
          data={data.results || []}
          pageCount={data.totalPages}
          filters={filters}
          onClick={ClickHandler}
          onChange={(page) => {
            setFilters({
              ...filters,
              page,
            });
            loadData({
              ...filters,
              page,
            });
          }}
          filterCom={<KnowledgeSharingFilters loadData={loadData} />}
          isLoading={isLoading}
        />
      </div>

      <Dialog
        open={isCreate}
        onClose={() => setIsCreate(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {editData?.id ? "Edit Knowledge" : "Share Knowledge"}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" alignItems="center" gap={2}>
            <Box flex={1}>
              <FormGenerator
                config={getKnowledgeSharingFormConfig()}
                formData={formData}
                setFormData={setFormData}
                errors={errors}
                setErrors={setErrors}
                fieldGridSize={12}
              />
            </Box>
            <Box
              flex={1}
              display="flex"
              flexDirection="column"
              alignItems="flex-start"
            >
              <label style={{ fontWeight: 500, marginBottom: 8 }}>
                Document *
              </label>
              <Box display="flex" alignItems="center" gap={1}>
                <SiFileUpload id={null} callBack={callBack} />
              </Box>
            </Box>
          </Box>
          <Box sx={{ mt: 3 }}>
            {files[0] && (
              <a href={files[0]?.url} target="_blank">
                {getFileNameFromUrl(files[0]?.url, 50)}
              </a>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <CustomButtonComponent
            variant="outlined"
            onClick={() => setIsCreate(false)}
          >
            Cancel
          </CustomButtonComponent>
          <CustomButtonComponent
            variant="contained"
            color="success"
            onClick={submitHandler}
          >
            {editData?.id ? "Update" : "Submit"}
          </CustomButtonComponent>
        </DialogActions>
      </Dialog>

      {isDelete?.isOpen && (
        <DeleteModal
          deleteHandler={deleteHandler}
          onClose={() => {
            setIsDelete({ isOpen: false, data: {} });
          }}
        />
      )}
    </div>
  );
};

export default KnowledgeSharing;
